import React, { useEffect, useRef, useState } from "react";

// Smoothly tween a numeric value for live "telemetry" feel.
export function AnimatedNumber({ value, decimals = 0, className = "" }) {
  const [display, setDisplay] = useState(Number(value) || 0);
  const ref = useRef(Number(value) || 0);
  const raf = useRef(null);

  useEffect(() => {
    const from = ref.current;
    const to = Number(value) || 0;
    const start = performance.now();
    const dur = 600;
    cancelAnimationFrame(raf.current);
    const step = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = from + (to - from) * eased;
      ref.current = cur;
      setDisplay(cur);
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value]);

  return (
    <span className={`tnum ${className}`}>
      {display.toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
    </span>
  );
}

export function Panel({ title, eyebrow, right, className = "", children, testid }) {
  return (
    <div data-testid={testid} className={`panel ${className}`}>
      {(title || eyebrow || right) && (
        <div className="flex items-center justify-between px-4 pt-3.5 pb-3 border-b border-border">
          <div>
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            {title && <div className="font-display font-bold text-sm tracking-tight uppercase text-ink mt-0.5">{title}</div>}
          </div>
          {right}
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );
}

export function StatusPill({ status, label }) {
  const map = {
    critical: { bg: "rgba(230,57,70,0.14)", c: "#ff6b75", t: label || "Critical" },
    warning: { bg: "rgba(250,178,11,0.14)", c: "#fab20b", t: label || "Attention" },
    nominal: { bg: "rgba(51,183,109,0.14)", c: "#33b76d", t: label || "Nominal" },
  };
  const s = map[status] || map.nominal;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.66rem] font-bold uppercase tracking-wider font-mono"
      style={{ background: s.bg, color: s.c, border: `1px solid ${s.c}40` }}>
      <span className="live-dot inline-block w-1.5 h-1.5 rounded-full" style={{ background: s.c }} />
      {s.t}
    </span>
  );
}

export function KpiStat({ label, value, unit, color = "#FFFFFF", decimals = 0, sub, testid }) {
  return (
    <div data-testid={testid} className="panel px-4 py-3 hover:border-brand-green/40 transition-colors duration-200">
      <div className="eyebrow truncate">{label}</div>
      <div className="flex items-baseline gap-1.5 mt-1.5">
        <span className="text-2xl font-bold leading-none" style={{ color }}>
          <AnimatedNumber value={value} decimals={decimals} />
        </span>
        {unit && <span className="text-[0.7rem] text-ink-soft font-mono">{unit}</span>}
      </div>
      {sub && <div className="text-[0.68rem] text-ink-soft mt-1 font-mono">{sub}</div>}
    </div>
  );
}
